import { useState, useCallback, useMemo } from 'react';
import {
  DifficultyCategory,
  DifficultyConfig,
  DIFFICULTY_CATEGORIES,
} from '../../types/problems';
import RangeSlider from '../common/RangeSlider';
import { Button } from '../common/Button';

export interface DifficultySelectorProps {
  // T045: Updated to pass category for mid-session slider bounds tracking
  onStartSession: (minDifficulty: number, maxDifficulty: number, category?: DifficultyCategory) => void;
  isLoading?: boolean;
}

/**
 * Component for selecting difficulty level category and range.
 * Users select one of four categories (Beginner, Intermediate, Advanced, Expert).
 * Non-Beginner categories show a dual-handle slider for fine-tuning the range.
 */
export default function DifficultySelector({
  onStartSession,
  isLoading = false,
}: DifficultySelectorProps) {
  const [selectedCategory, setSelectedCategory] = useState<DifficultyCategory | null>(null);
  const [customRange, setCustomRange] = useState<{ min: number; max: number } | null>(null);

  // Get the config for the selected category
  const categoryConfig: DifficultyConfig | null = useMemo(() => {
    if (!selectedCategory) return null;
    return DIFFICULTY_CATEGORIES[selectedCategory];
  }, [selectedCategory]);

  // Current range (either custom or category default)
  const currentRange = useMemo(() => {
    if (!categoryConfig) return null;
    if (customRange) return customRange;
    return { min: categoryConfig.min, max: categoryConfig.max };
  }, [categoryConfig, customRange]);

  // Handle category selection
  const handleCategorySelect = useCallback((category: DifficultyCategory) => {
    setSelectedCategory(category);
    // Reset custom range when switching categories
    setCustomRange(null);
  }, []);

  // Handle range change from slider
  const handleRangeChange = useCallback((min: number, max: number) => {
    setCustomRange({ min, max });
  }, []);

  // Handle start session
  // T045: Pass category for mid-session slider bounds tracking
  const handleStartSession = useCallback(() => {
    if (!currentRange || !selectedCategory) return;
    onStartSession(currentRange.min, currentRange.max, selectedCategory);
  }, [currentRange, selectedCategory, onStartSession]);

  // Generate labels for the slider based on category range
  const sliderLabels = useMemo(() => {
    if (!categoryConfig) return [];
    const labels: number[] = [];
    for (let i = categoryConfig.min; i <= categoryConfig.max; i++) {
      labels.push(i);
    }
    return labels;
  }, [categoryConfig]);

  // Category buttons configuration
  const categories: { key: DifficultyCategory; label: string }[] = [
    { key: 'beginner', label: 'Beginner' },
    { key: 'intermediate', label: 'Intermediate' },
    { key: 'advanced', label: 'Advanced' },
    { key: 'expert', label: 'Expert' },
  ];

  return (
    <div className="w-full max-w-md mx-auto p-6 bg-card rounded-lg shadow-md">
      <h2 className="text-xl font-semibold text-ink mb-4 text-center">
        Select Difficulty Level
      </h2>

      {/* Category buttons */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        {categories.map(({ key, label }) => {
          const isSelected = selectedCategory === key;
          const config = DIFFICULTY_CATEGORIES[key];

          return (
            <button
              key={key}
              onClick={() => handleCategorySelect(key)}
              className={`
                px-4 py-3 rounded-lg font-medium transition-all duration-200
                border-2 text-center
                ${isSelected
                  ? 'bg-primary-600 text-white border-primary-600 shadow-md'
                  : 'bg-card text-ink border-line-strong hover:border-primary-400 hover:bg-primary-50'
                }
              `}
              aria-pressed={isSelected}
            >
              <span className="block text-base">{label}</span>
              <span className={`block text-xs mt-1 ${isSelected ? 'text-primary-100' : 'text-ink-soft'}`}>
                Levels {config.min}-{config.max}
              </span>
            </button>
          );
        })}
      </div>

      {/* Range selector - only show for non-Beginner categories */}
      {categoryConfig && (
        <div className="mb-6">
          {categoryConfig.hasSlider ? (
            <div className="bg-cream rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm font-medium text-ink">Adjust Range</span>
                <span className="text-sm font-bold text-primary-600">
                  {currentRange?.min} - {currentRange?.max}
                </span>
              </div>
              <RangeSlider
                min={categoryConfig.min}
                max={categoryConfig.max}
                minValue={currentRange?.min ?? categoryConfig.min}
                maxValue={currentRange?.max ?? categoryConfig.max}
                labels={sliderLabels}
                onChange={handleRangeChange}
              />
            </div>
          ) : (
            <div className="bg-primary-50 rounded-lg p-4 text-center">
              <span className="text-sm text-primary-700">
                Fixed range: Levels {categoryConfig.min}-{categoryConfig.max}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Selected range summary */}
      {currentRange && (
        <div className="mb-4 p-3 bg-cream rounded-lg text-center">
          <span className="text-sm text-ink-soft">Selected Range: </span>
          <span className="text-sm font-bold text-ink">
            Level {currentRange.min}
            {currentRange.min !== currentRange.max && ` to ${currentRange.max}`}
          </span>
        </div>
      )}

      {/* Start Practice button - T016: disabled when no category selected */}
      <Button
        onClick={handleStartSession}
        disabled={!selectedCategory || isLoading}
        variant="primary"
        className="w-full py-3 text-lg"
      >
        {isLoading ? 'Starting...' : 'Start Practice'}
      </Button>

      {/* Hint when no category selected */}
      {!selectedCategory && (
        <p className="text-center text-sm text-ink-soft mt-3">
          Select a difficulty level to begin
        </p>
      )}
    </div>
  );
}
